import { useQuery } from "@apollo/client";
import { uniqBy } from "lodash";

import useUserComboboxQuery from "@components/user-combobox/use-user-combobox-query";
import UserCombobox from "@components/user-combobox/user-combobox";
import {
  UserComboboxOption,
  UserComboboxOptionType,
  UserComboboxUserOption,
} from "@components/user-combobox/user-combobox-list";
import getUserQuery from "@graphql/get-user-query";
import { graphqlNone } from "@helpers/constants";

import ExplorerComboboxButton from "./explorer-combobox-button";

const ExplorerUserCombobox = ({
  userId,
  prefix = "",
  prefixTooltip = "",
  ariaLabel = "Explorer user combobox",
  onSelectUserId,
  unassignedText = "No assignee",
  className = "",
  hasUnassignedOption = false,
}: {
  userId?: number | null;
  prefix?: string;
  prefixTooltip?: string;
  ariaLabel?: string;
  onSelectUserId: (userId: number | null) => void;
  unassignedText?: string;
  allText?: string;
  className: string;
  hasUnassignedOption?: boolean;
}) => {
  const unassignedOption: UserComboboxUserOption = {
    name: unassignedText,
    id: graphqlNone,
    type: UserComboboxOptionType.USER,
  };

  const { data } = useQuery(getUserQuery, {
    fetchPolicy: "cache-first",
    variables: { userId },
    skip: !userId || userId === unassignedOption.id,
  });
  const selectedUser =
    userId === unassignedOption.id
      ? unassignedOption
      : data
      ? { type: UserComboboxOptionType.USER, ...data.user }
      : null;

  const { loading, options, query, setQuery } = useUserComboboxQuery({
    selected: selectedUser,
    emptyOption: hasUnassignedOption ? unassignedOption : undefined,
  });
  const validOptions = uniqBy([...options], "id");

  const handleChangeValue = (option: UserComboboxOption) => {
    onSelectUserId(option.id as number);
  };

  const handleClearValue = () => {
    onSelectUserId(null);
  };

  return (
    <div className="flex-1 min-w-0">
      <UserCombobox
        aria-label={ariaLabel}
        loading={loading}
        placeholder="All"
        query={query}
        value={selectedUser}
        options={validOptions}
        clearable={!!selectedUser}
        onChangeValue={handleChangeValue}
        onChangeQuery={setQuery}
        onClearValue={handleClearValue}
      >
        {({ value, onClickButton, clearable, setReferenceElement }) => {
          return !value || value?.type === UserComboboxOptionType.USER ? (
            <ExplorerComboboxButton
              prefix={prefix}
              prefixTooltip={prefixTooltip}
              ariaLabel="Explorer user picker button"
              setReferenceElement={setReferenceElement}
              onClickButton={onClickButton}
              label={value?.name || "All"}
              clearable={clearable}
              onClear={handleClearValue}
              className={className}
              avatarUser={
                value && value.id !== unassignedOption.id ? value : undefined
              }
            />
          ) : null;
        }}
      </UserCombobox>
    </div>
  );
};

export default ExplorerUserCombobox;
